import { type FC, type ReactElement, useCallback } from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import { LenssourceModelLensSourceType } from "../../../gql-generated/types";
import { useLoadGroupsV2 } from "../../groups/containers/useLoadGroupsV2";
import { GroupsRepositoryV2 } from "../../groups/model";
import { useLoadLensesV2 } from "../../lenses/containers/useLoadLensesV2";
import { LensesRepositoryV2 } from "../../lenses/models";
import { LensSourcesRepository } from "../api/cache";
import { updateLensSources as updateLensSourcesMutation } from "../api/mutations/updateLensSources";
import {
    type UpdateLensSourcesMutation,
    type UpdateLensSourcesMutationVariables,
} from "../api/mutations/updateLensSources.gql-generated";
import { useLensSources } from "../hooks/useLensSources";
import { type SourceModel } from "../models";
import { GROUPS_QUERY_CACHE_KEY } from "../../groups/api/cache/GroupsPagination";

interface UpdateLensSourceApi {
    updating: boolean;
    enable: (sources: SourceModel[]) => Promise<unknown>;
    disable: (sources: SourceModel[]) => Promise<unknown>;
}

interface UpdateLensSourceContainerProps {
    children: (api: UpdateLensSourceApi) => ReactElement;
    selectedType: LenssourceModelLensSourceType;
}

export const UpdateLensSourceContainer: FC<UpdateLensSourceContainerProps> = ({ children, selectedType }) => {
    const { reset: resetAllLenses } = useLoadLensesV2(false);
    const { reset: resetLensSources } = useLensSources(selectedType);
    const { reset: resetAllGroups } = useLoadGroupsV2(false);
    const client = useApolloClient();

    const [updateLensSources, { loading }] = useMutation<UpdateLensSourcesMutation, UpdateLensSourcesMutationVariables>(
        updateLensSourcesMutation,
        {
            refetchQueries: ["getLensSourceListEnabled"],
            update: () => {
                LensSourcesRepository.initDefaults(client.cache, selectedType);
                LensesRepositoryV2.initDefaults(client.cache);
                resetAllLenses();
                resetLensSources();
            },
        },
    );

    const updateSources = useCallback(
        (sources: SourceModel[], enableSources: boolean) =>
            updateLensSources({
                variables: {
                    lensSourceUpdates: sources.map(({ externalId, id }) => ({
                        enabled: enableSources,
                        externalId,
                        lensSourceId: id,
                    })),
                },
                update: (cache) => {
                    if (!enableSources) {
                        GroupsRepositoryV2.removeAllGroupsConnections(client);
                        GroupsRepositoryV2.initDefaults(client.cache);
                        resetAllGroups();
                        cache.evict({ fieldName: GROUPS_QUERY_CACHE_KEY });
                        cache.gc();
                    }
                },
            }),
        [client, resetAllGroups, updateLensSources],
    );

    const enable = useCallback((sources: SourceModel[]) => updateSources(sources, true), [updateSources]);
    const disable = useCallback((sources: SourceModel[]) => updateSources(sources, false), [updateSources]);

    return children({ updating: loading, enable, disable });
};
